import {
  KIOSKS_API_URL,
  KIOSKS_FILTER_URL,
  KIOSKS_LOCATION_ID_CHECK_URL,
  KIOSKS_SEARCH_FIND_AVAILABLE_RELAYS_URL,
  KIOSKS_SEARCH_FIND_ALL_BY_DEALER_PAYMENT_TYPES_URL,
} from 'constants/ApiConstants';
import type { ApiError, MetaAction } from 'modules/global/generic.types';
import type { KioskStatus } from 'modules/kiosks/constants';
import { SELECT_SOURCE } from 'modules/kiosks/constants';

import * as types from './actionTypes';
import type * as KioskTypes from './types';

export const fetchKiosks = (
  params: KioskTypes.KiosksGetRequestParams,
  meta: MetaAction = {}
): KioskTypes.FetchKiosksAction => ({
  type: types.FETCH_KIOSKS,
  payload: {
    request: {
      method: 'GET',
      url: KIOSKS_FILTER_URL,
      params,
    },
  },
  meta,
});

export const fetchKiosksSelect = (
  params: KioskTypes.KiosksGetRequestParams,
  meta: MetaAction = {}
): KioskTypes.FetchKiosksAction =>
  fetchKiosks(params, {
    ...meta,
    source: SELECT_SOURCE,
  });

export const fetchKiosksSuccess = (
  data: KioskTypes.KiosksPage,
  meta: MetaAction = {}
): KioskTypes.FetchKiosksSuccessAction => ({
  type: types.FETCH_KIOSKS_SUCCESS,
  payload: data,
  meta,
});

export const fetchKiosksFailure = (error: ApiError, meta: MetaAction = {}): KioskTypes.FetchKiosksFailureAction => ({
  type: types.FETCH_KIOSKS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchKiosk = (kioskId: string, meta: MetaAction = {}): KioskTypes.FetchKioskAction => ({
  type: types.FETCH_KIOSK,
  payload: {
    request: {
      method: 'GET',
      url: `${KIOSKS_API_URL}/${kioskId}`,
    },
  },
  meta,
});

export const fetchKioskSuccess = (
  kiosk: KioskTypes.DetailedKiosk,
  meta: MetaAction = {}
): KioskTypes.FetchKioskSuccessAction => ({
  type: types.FETCH_KIOSK_SUCCESS,
  payload: kiosk,
  meta,
});

export const fetchKioskFailure = (error: ApiError, meta: MetaAction = {}): KioskTypes.FetchKioskFailureAction => ({
  type: types.FETCH_KIOSK_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const createKiosk = (
  kiosk: KioskTypes.KioskCreateData,
  meta: MetaAction = {}
): KioskTypes.CreateKioskAction => ({
  type: types.POST_KIOSK,
  payload: {
    request: {
      method: 'POST',
      url: KIOSKS_API_URL,
      data: kiosk,
    },
  },
  meta,
});

export const createKioskSuccess = (
  kiosk: KioskTypes.DetailedKiosk,
  meta: MetaAction = {}
): KioskTypes.CreateKioskSuccessAction => ({
  type: types.POST_KIOSK_SUCCESS,
  payload: kiosk,
  meta,
});

export const createKiosksFailure = (error: ApiError, meta: MetaAction = {}): KioskTypes.CreateKioskFailureAction => ({
  type: types.POST_KIOSK_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const updateKiosk = (kiosk: KioskTypes.DetailedKiosk, meta: MetaAction = {}): KioskTypes.PutKioskAction => ({
  type: types.PUT_KIOSK,
  payload: {
    request: {
      method: 'PUT',
      url: `${KIOSKS_API_URL}/${kiosk.id}`,
      data: kiosk,
    },
  },
  meta,
});

export const updateKioskSuccess = (
  kiosk: KioskTypes.DetailedKiosk,
  meta: MetaAction = {}
): KioskTypes.PutKioskSuccessAction => ({
  type: types.PUT_KIOSK_SUCCESS,
  payload: kiosk,
  meta,
});

export const updateKioskFailure = (error: ApiError, meta: MetaAction = {}): KioskTypes.PutKioskFailureAction => ({
  type: types.PUT_KIOSK_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const setKioskDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {}
): KioskTypes.SetKioskDashboardFilterAction => ({
  type: types.SET_KIOSKS_DASHBOARD_FILTER,
  payload: { field, value },
  meta,
});

export const removeKioskDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {}
): KioskTypes.RemoveKioskDashboardFilterAction => ({
  type: types.REMOVE_KIOSKS_DASHBOARD_FILTER,
  payload: {
    field,
    value,
  },
  meta,
});

export const clearKioskDashboardFilters = (meta: MetaAction = {}): KioskTypes.ClearKioskDashboardFiltersAction => ({
  type: types.CLEAR_KIOSKS_DASHBOARD_FILTER,
  meta,
});

export const patchKioskStatus = (
  { kioskId, status }: { kioskId: string; status: KioskStatus },
  meta: MetaAction = {}
): KioskTypes.PatchKioskStatusAction => ({
  type: types.PATCH_STATUS_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${KIOSKS_API_URL}/${kioskId}/status`,
      data: { status },
    },
    kioskId,
  },
  meta,
});

export const patchKioskStatusFailure = (
  error: ApiError,
  meta: MetaAction = {}
): KioskTypes.PatchKioskStatusFailureAction => ({
  type: types.PATCH_STATUS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const deleteKiosk = (kioskId: string, meta: MetaAction = {}): KioskTypes.DeleteKioskAction => ({
  type: types.DELETE_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${KIOSKS_API_URL}/${kioskId}`,
    },
    kioskId,
  },
  meta,
});

export const deleteKioskSuccess = (
  kioskId: string,
  meta: MetaAction = {}
): KioskTypes.DeleteKioskSuccessAction => ({
  type: types.DELETE_SUCCESS,
  payload: kioskId,
  meta,
});

export const deleteKioskFailure = (error: ApiError, meta: MetaAction = {}): KioskTypes.DeleteKioskFailureAction => ({
  type: types.DELETE_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const configureKiosk = (kioskId: string, meta: MetaAction = {}): KioskTypes.ConfigureKioskAction => ({
  type: types.CONFIGURE_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${KIOSKS_API_URL}/${kioskId}/configure`,
    },
  },
  meta,
});

export const getKiosksByPaymentTypes = (
  dealerId: string,
  params: Omit<KioskTypes.KiosksByPaymentTypesGetRequestParams, 'dealerId'>,
  meta: MetaAction = {}
): KioskTypes.FetchKiosksByPaymentTypesAction => ({
  type: types.FETCH_KIOSKS_BY_PAYMENT_TYPES,
  payload: {
    dealerId,
    request: {
      method: 'GET',
      url: KIOSKS_SEARCH_FIND_ALL_BY_DEALER_PAYMENT_TYPES_URL,
      params: {
        ...params,
        dealerId,
      },
    },
  },
  meta,
});

export const getKiosksByPaymentTypesSuccess = (
  dealerId: string,
  kiosks: KioskTypes.DetailedKiosksPage,
  meta: MetaAction = {}
): KioskTypes.FetchKiosksByPaymentTypesSuccessAction => ({
  type: types.FETCH_KIOSKS_BY_PAYMENT_TYPES_SUCCESS,
  payload: { dealerId, kiosks },
  meta,
});

export const getKioskURL = (kioskId: string): KioskTypes.FetchKioskURLAction => ({
  type: types.FETCH_KIOSK_URL,
  payload: {
    request: {
      method: 'GET',
      url: `${KIOSKS_API_URL}/${kioskId}/so/kiosk/url`,
    },
  },
  meta: { '@app-request': true },
});

export const getKioskMasterPin = (kioskId: string): KioskTypes.FetchKioskMasterPinAction => ({
  type: types.FETCH_KIOSK_MASTER_PIN,
  payload: {
    request: {
      method: 'GET',
      url: `${KIOSKS_API_URL}/${kioskId}/masterCode`,
    },
  },
  meta: { '@app-request': true },
});

export const getKioskSupportCode = (kioskId: string): KioskTypes.FetchKioskSupportCodeAction => ({
  type: types.FETCH_KIOSK_SUPPORT_CODE,
  payload: {
    request: {
      method: 'GET',
      url: `${KIOSKS_API_URL}/${kioskId}/supportCode`,
    },
  },
  meta: { '@app-request': true },
});

export const setIsReady = (meta: MetaAction = {}): KioskTypes.SetIsReadyAction => ({
  type: types.SET_IS_READY,
  meta,
});

export const requestKioskStatus = (kioskId: string): KioskTypes.RequestKioskStatusAction => ({
  type: types.REQUEST_KIOSK_STATUS,
  payload: {
    request: {
      method: 'GET',
      url: `${KIOSKS_API_URL}/${kioskId}/status`,
    },
  },
});

export const requestKioskStatusSuccess = (
  payload: KioskTypes.KioskStatusSocketPayload,
  meta: MetaAction = {}
): KioskTypes.RequestKioskStatusSuccessAction => ({
  type: types.REQUEST_KIOSK_STATUS_SUCCESS,
  payload,
  meta,
});

export const requestKioskStatusFailure = (
  error: ApiError | Error,
  meta: MetaAction = {}
): KioskTypes.RequestKioskStatusFailureAction => ({
  type: types.REQUEST_KIOSK_STATUS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const resetKioskStatus = (kioskId: string): KioskTypes.ResetKioskStatusAction => ({
  type: types.RESET_KIOSK_STATUS,
  payload: {
    request: {
      method: 'POST',
      url: `${KIOSKS_API_URL}/${kioskId}/reset`,
    },
  },
});

export const fetchAvailableRelays = (
  params: KioskTypes.AvailableRelaysGetRequestParams,
  meta: MetaAction = {}
): KioskTypes.FetchAvailableRelaysAction => ({
  type: types.FETCH_AVAILABLE_RELAYS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: KIOSKS_SEARCH_FIND_AVAILABLE_RELAYS_URL,
      params,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

export const fetchAvailableRelaysSuccess = (
  data: KioskTypes.DetailedKiosksPage,
  meta: MetaAction = {}
): KioskTypes.FetchAvailableRelaysSuccessAction => ({
  type: types.FETCH_AVAILABLE_RELAYS_SUCCESS,
  payload: data,
  meta,
});

export const fetchAvailableRelaysFailure = (
  error: ApiError,
  meta: MetaAction = {}
): KioskTypes.FetchAvailableRelaysFailureAction => ({
  type: types.FETCH_AVAILABLE_RELAYS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const checkKioskLocationIdExists = (
  locationId: number,
  meta: MetaAction = {}
): KioskTypes.CheckKioskLocationIdAction => ({
  type: types.CHECK_KIOSK_LOCATION_ID_REQUEST,
  payload: {
    request: {
      method: 'HEAD',
      url: KIOSKS_LOCATION_ID_CHECK_URL,
      params: { locationId },
    },
  },
  meta,
});
